import {
    useCallback, useRef,
} from 'react'
import {
    FormControl,
} from '@mui/material'
import noop from 'lodash/noop'
import isEmpty from 'lodash/isEmpty'

import useEnumValues from 'app/hooks/useEnumValues'
import Checkbox from 'app/shared-components/Checkbox'
import Label from 'app/shared-components/Label'
import EnumSelector from 'app/shared-components/EnumSelector'
import useLabel from 'app/hooks/useLabel'
import {
    ContainerAvailabilityAllowedProductTypes,
} from 'app/enums/label.enums'
import {
    ForecastAvailabilityOptions,
} from '../../../serviceCenters.types'

import useStyles from './OptionPanel.style'

const defaultProps = {
    includesOrders: undefined,
    setIncludesOrders: noop,
}

const OptionPanel = ({
    value,
    onChange,
    allowedValues,
    includesOrders,
    setIncludesOrders,
}: {
    value: ForecastAvailabilityOptions,
    onChange: (value: ForecastAvailabilityOptions) => void,
    allowedValues: Record<string, string>,
    includesOrders?: Record<string, boolean>,
    setIncludesOrders?: (newVal: Record<string, boolean>) => void,
}) => {
    const {
        classes,
    } = useStyles()

    const l = useLabel()

    const listStatuses = useRef(Object.keys(value.containerStatus))

    const onChangeIncludesOrders = useCallback((newVal: boolean, {
        target: {
            name,
        },
    }) => {
        setIncludesOrders({
            ...includesOrders,
            [name]: newVal,
        })
    }, [
        includesOrders,
        setIncludesOrders,
    ])

    const onChangeContainerStatus = useCallback((newVal: boolean, {
        target: {
            name,
        },
    }) => {
        onChange({
            ...value,
            containerStatus: {
                ...value.containerStatus,
                [name]: newVal,
            },
        })
    }, [
        onChange,
        value,
    ])

    const onChangeProductType = useCallback((newVal: ContainerAvailabilityAllowedProductTypes) => {
        onChange({
            ...value,
            productType: newVal,
        })
    }, [
        onChange,
        value,
    ])

    const allowedProductTypes = useEnumValues()(allowedValues)

    return (
        <div className={classes.panel}>
            <FormControl className={classes.containerStatus}>
                <Label title={l('Container.containerStatus')} />
                <div className={classes.wrapperCheckbox}>
                    {
                        listStatuses.current.map((item) => {
                            return (
                                <Checkbox
                                    key={item}
                                    name={item}
                                    title={l(`Monitoring.${item}`)}
                                    value={value.containerStatus[item]}
                                    onChange={onChangeContainerStatus}
                                    className={classes.checkbox}
                                />
                            )
                        })
                    }
                </div>
            </FormControl>
            <EnumSelector
                title={l('Forecasts.productType')}
                className={classes.productType}
                value={value.productType}
                allowedValues={allowedProductTypes}
                onChange={onChangeProductType}
                name="productType"
                useDropdown
                noNone
                noSort
            />
            {includesOrders && !isEmpty(includesOrders) && (
                <FormControl className={classes.containerStatus}>
                    <Label title={l('ServiceCenter.includesOrders')} />
                    <div className={classes.wrapperCheckbox}>
                        {
                            Object.keys(includesOrders).map((key) => {
                                return (
                                    <Checkbox
                                        name={key}
                                        title={l(`ServiceCenter.${key}`)}
                                        value={includesOrders?.[key]}
                                        onChange={onChangeIncludesOrders}
                                        className={classes.checkbox}
                                        key={key}
                                    />
                                )
                            })
                        }
                    </div>
                </FormControl>
            )}
        </div>
    )
}

OptionPanel.defaultProps = defaultProps

export default OptionPanel
